import { formatGazeReturn } from "@talenthop/shared";
import type { IntegrityEventKind } from "@talenthop/shared";
import { downloadCost, frameReadable, shouldLoadLocalModel } from "./faceDetection.js";
import type { DownloadCost } from "./faceDetection.js";

/**
 * Candidate · STRICT-mode gaze/attention detection. Mirrors faceDetection.ts exactly: a pure,
 * unit-tested machine here; the heavy MediaPipe FaceLandmarker behind a dynamic import in
 * gazeDetectionModel.ts; every reading through a guard that turns an unreadable frame into null,
 * never into an accusation. Everything is on-device — no frame, landmark, blendshape or matrix
 * ever leaves the candidate's machine, same rule as face presence and speech.
 *
 * ONE DETECTOR IN STRICT MODE. The landmarker reports how many faces it found AND where the one
 * face is pointing, so strict sessions run it INSTEAD of BlazeFace — never both. Two wasm heaps
 * plus two models competing mid-interview is a stutter the candidate would feel (see
 * visionRuntime.ts), and faces-from-the-landmarker feeds the existing presence machine unchanged.
 *
 * WHAT GAZE IS HERE — and, more importantly, what it is not:
 *   • An OBSERVATION for the hiring team, never an accusation. The estimator is generous by
 *     design (when in doubt → "screen"), the machine debounces harder than face presence, and
 *     the shared penalty for a confirmed episode is smaller than a tab switch. The report carries
 *     a fairness caveat; nothing here says or implies "cheating".
 *   • Measured ONLY while the candidate is actively answering. The hook feeds this machine null
 *     outside the speaking window, so thinking time — staring at the ceiling, reading notes,
 *     closing your eyes to structure an answer — is explicitly free.
 *   • NEVER shown live to the candidate. This is deliberate and different from the camera-framing
 *     notice: that notice helps them fix something fixable (step back into frame); a "we saw you
 *     look away" nag mid-answer fixes nothing, coaches candidates into staring rigidly at the
 *     lens, and rattles exactly the honest ones. Disclosure lives at consent, where the mode is
 *     named; findings live in the report, beside the caveat, for a human.
 *   • Inadmissible without proof. gaze_away rows only ever cost a point when a gaze_watch_on
 *     heartbeat proves the estimator genuinely ran (enforced in shared computePresence) — no
 *     detector means no data, never a fabricated "all clear" and never a browser-shaped penalty.
 */

// ── the detector seam ─────────────────────────────────────────────────────────

export type GazeDirection = "left" | "right" | "down";
export type GazeStatus = "screen" | GazeDirection;

/** One tick's worth of what the landmarker saw. `gaze` is null whenever `faces !== 1` — with
 *  nobody or two people in frame, "where is the candidate looking" has no referent that tick. */
export interface GazeReading {
  faces: number;
  gaze: GazeStatus | null;
}

export interface GazeFaceSource {
  readonly engine: "mediapipe-landmarker";
  /** null = NO READING (no decoded frame, camera off/denied, detector hiccup, source closed).
   *  Same contract as guardedFaceRead: null is emphatically not "absent" and not "away". */
  read(video: HTMLVideoElement): Promise<GazeReading | null>;
  close(): void;
}

/**
 * The ONE way the landmarker branch may produce a reading — gazeDetectionModel.ts reads through
 * this, exactly as faceDetectionModel.ts reads through guardedFaceRead, and for the same shipped
 * bug: MediaPipe probes an undecoded <video> as [0,0] and answers an empty result without
 * throwing, so an unguarded read manufactures "0 faces" out of a camera that never came on.
 */
export function guardedGazeRead(
  video: HTMLVideoElement,
  detect: (video: HTMLVideoElement) => GazeReading | null | undefined,
): GazeReading | null {
  if (!frameReadable(video)) return null;
  let reading: GazeReading | null | undefined;
  try {
    reading = detect(video);
  } catch {
    return null;
  }
  if (!reading) return null;
  const { faces } = reading;
  return typeof faces === "number" && Number.isFinite(faces) && faces >= 0 ? reading : null;
}

/**
 * Pick the gaze-capable local engine, or null. No native branch exists — the Shape Detection API
 * counts faces but cannot say where one is pointing — so this is MediaPipe or honest silence.
 *
 * The download gate is shared with face presence (shouldLoadLocalModel): strict mode does not
 * change the economics of a metered phone connection, so a save-data candidate simply produces no
 * gaze data and no face data, the report says neither was observed, and the strict UX upstream
 * must degrade rather than dead-end (a gate that cannot run must never trap the candidate).
 */
export async function createGazeFaceSource(
  env: Record<string, unknown> = (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {},
  cost: DownloadCost = downloadCost(),
): Promise<GazeFaceSource | null> {
  if (typeof window === "undefined") return null; // SSR / unit tests: never reach for the model
  if (!shouldLoadLocalModel(env, cost)) return null;
  try {
    const { createMediaPipeGazeSource } = await import("./gazeDetectionModel.js");
    return await createMediaPipeGazeSource();
  } catch {
    return null; // chunk failed to load (offline, blocked) — no data, no claim
  }
}

// ── the estimator (pure) ──────────────────────────────────────────────────────

/** Structural subset of MediaPipe's FaceLandmarkerResult — structural so the estimator stays a
 *  plain function of plain values and the tests never need the wasm runtime. */
export interface GazeEstimateInput {
  faceLandmarks: readonly unknown[];
  faceBlendshapes?: readonly { categories: readonly { categoryName: string; score: number }[] }[];
  facialTransformationMatrixes?: readonly { rows: number; columns: number; data: readonly number[] }[];
}

/**
 * THRESHOLDS, and why they are set where they are. Every number below decides whether a real
 * person gets an attention flag for being a person, so all of them err toward "screen":
 *
 *   • 28° of head yaw / 22° of downward pitch before the head alone reads as away. Normal
 *     conversational head movement — nodding, tilting, glancing at a second of thought — lives
 *     well inside ±15°; 28° is a head genuinely turned toward something. A candidate with a
 *     slightly angled camera, a wheelchair-height desk setup, or torticollis must not spend an
 *     interview being flagged for their geometry.
 *   • Eye blendshapes only ever REINFORCE, at 18°/14° head angles with the matching eye-look sum
 *     over 0.7 — i.e. head noticeably turned AND both eyes pushed the same way. Eyes-only counts
 *     solely when extreme (lateral sum ≥ 1.4 of a possible 2.0: both eyes pegged sideways, the
 *     posture of reading a second monitor without turning). Ordinary saccades, glasses refraction
 *     and strabismus all score far below that.
 *   • Eyes-down alone NEVER counts, at any strength. The live transcript and the question text
 *     sit at the bottom of the candidate's own screen — looking down with a level head is very
 *     often looking at US. Only a genuinely dropped head (pitch) reads as "down".
 *   • Anything unreadable — no matrix, malformed matrix, missing blendshapes — collapses to
 *     "screen", never to a guess. And with faces !== 1 the question is meaningless: null.
 *
 * Gaze patterns differ across people — glasses, lighting, camera placement, neurodivergence —
 * which is why the report copy carries that caveat verbatim and a human reads these as context.
 */
export const HEAD_YAW_AWAY_DEG = 28;
export const HEAD_PITCH_DOWN_AWAY_DEG = 22;
export const REINFORCED_YAW_DEG = 18;
export const REINFORCED_PITCH_DEG = 14;
export const EYE_REINFORCE_SUM = 0.7;
export const EYE_ONLY_LATERAL_SUM = 1.4;

/** Blendshape scores by name, defaulting to 0 — a browser/model that omits blendshapes simply
 *  contributes no eye evidence, which biases toward "screen", the safe direction. */
function blendScores(input: GazeEstimateInput): Record<string, number> {
  const out: Record<string, number> = {};
  const cats = input.faceBlendshapes?.[0]?.categories;
  if (!cats) return out;
  for (const c of cats) out[c.categoryName] = c.score;
  return out;
}

/**
 * Head pose from the 4x4 facial transformation matrix (COLUMN-MAJOR data, as MediaPipe emits it —
 * the same layout three.js's Matrix4.fromArray assumes, which is how every MediaPipe web demo
 * consumes this field). The canonical face looks along +Z, so the rotated third column IS the
 * face's forward vector; yaw/pitch fall out of it without a full Euler decomposition:
 *   yaw   > 0 → face turned toward the candidate's own left
 *   pitch > 0 → face dropped downward
 */
function headAngles(input: GazeEstimateInput): { yawDeg: number; pitchDownDeg: number } | null {
  const m = input.facialTransformationMatrixes?.[0];
  if (!m || m.rows !== 4 || m.columns !== 4 || m.data.length !== 16) return null;
  const fx = m.data[8] ?? 0; // third column = rotated +Z = face forward
  const fy = m.data[9] ?? 0;
  const fz = m.data[10] ?? 0;
  if (fx === 0 && fy === 0 && fz === 0) return null;
  const yawDeg = (Math.atan2(fx, fz) * 180) / Math.PI;
  // Camera space is y-up, so a dropped head sends the forward vector's y negative.
  const pitchDownDeg = (-Math.atan2(fy, Math.hypot(fx, fz)) * 180) / Math.PI;
  return { yawDeg, pitchDownDeg };
}

/**
 * Where is the one face in frame pointing, coarsely? "screen" | "left" | "right" | "down", or
 * null when there is not exactly one face (the question has no referent that tick).
 *
 * The direction label is CONTEXT for a reviewer ("mostly to the left" reads differently from
 * "every direction"), never evidence on its own — a mirrored self-view or an off-centre camera
 * can flip or bias it, which is one more reason the away/screen decision leans on magnitude, not
 * side.
 */
export function estimateGazeStatus(input: GazeEstimateInput): GazeStatus | null {
  if (input.faceLandmarks.length !== 1) return null;

  const eyes = blendScores(input);
  // ARKit-style names: "Left"/"Right" is the SUBJECT's eye. Looking to your own left = left eye
  // out + right eye in; the two sums can never both be high for a single genuine glance.
  const leftSum = (eyes.eyeLookOutLeft ?? 0) + (eyes.eyeLookInRight ?? 0);
  const rightSum = (eyes.eyeLookOutRight ?? 0) + (eyes.eyeLookInLeft ?? 0);
  const downSum = (eyes.eyeLookDownLeft ?? 0) + (eyes.eyeLookDownRight ?? 0);

  const head = headAngles(input);
  const yaw = head?.yawDeg ?? 0;
  const pitchDown = head?.pitchDownDeg ?? 0;

  // Head alone, at an angle nobody holds by accident.
  if (yaw >= HEAD_YAW_AWAY_DEG) return "left";
  if (yaw <= -HEAD_YAW_AWAY_DEG) return "right";
  if (pitchDown >= HEAD_PITCH_DOWN_AWAY_DEG) return "down";

  // Head noticeably turned AND the eyes pushing the same way — the second-screen posture.
  if (yaw >= REINFORCED_YAW_DEG && leftSum > EYE_REINFORCE_SUM) return "left";
  if (yaw <= -REINFORCED_YAW_DEG && rightSum > EYE_REINFORCE_SUM) return "right";
  if (pitchDown >= REINFORCED_PITCH_DEG && downSum > EYE_REINFORCE_SUM) return "down";

  // Eyes alone, only when pegged — a sideways read without a head turn. Never for "down": the
  // transcript and question text live at the bottom of the candidate's own screen.
  if (leftSum >= EYE_ONLY_LATERAL_SUM) return "left";
  if (rightSum >= EYE_ONLY_LATERAL_SUM) return "right";

  return "screen"; // when in doubt, the doubt belongs to the candidate
}

// ── the debounce machine (pure) ───────────────────────────────────────────────

/** One integrity event the machine wants emitted, in the shared vocabulary. */
export interface GazeEmit {
  kind: Extract<IntegrityEventKind, "gaze_away" | "gaze_return">;
  detail: string;
}

export interface GazeMachine {
  /** What we have CONFIRMED. Starts at "screen": nobody is away until proven so. */
  confirmed: "screen" | "away";
  pending: "screen" | "away";
  pendingSince: number;
  pendingSamples: number;
  confirmedSince: number;
  /** 0 before the first real reading — guards run-stitching across blind gaps. */
  lastReadingAt: number;
  /** Latest direction seen in the current away run — becomes the gaze_away detail. */
  lastAwayDirection: GazeDirection | null;
  /** gaze_away events emitted so far this session, against GAZE_AWAY_EMIT_CAP. */
  awayEmits: number;
  /** The currently confirmed away episode actually produced a gaze_away — its return receipt is
   *  only owed if it did, so a capped-out episode never emits a dangling gaze_return. */
  openEmitted: boolean;
}

/**
 * Harder debounce than face presence, on purpose: an attention flag is more personal than a
 * framing flag, so a glance has to HOLD — the same direction-agnostic away state for ≥4s AND ≥4
 * consecutive samples — before it exists at all. Checking a watch, following a noise, glancing at
 * notes: all over well inside the window. Coming back clears fast and quietly.
 */
export const GAZE_SAMPLE_MS = 1_000; // fed by the same 1 Hz sampler as face presence
export const GAZE_CONFIRM_MS: Record<"screen" | "away", number> = { away: 4_000, screen: 1_500 };
export const GAZE_CONFIRM_SAMPLES: Record<"screen" | "away", number> = { away: 4, screen: 2 };

/**
 * A blind spell longer than this abandons whatever run OR episode was in progress. Blind gaps
 * here are routine, not exceptional — the machine is fed null the entire time the candidate is
 * not answering — so an away confirmed at the end of one answer must never be stitched onto the
 * next answer's first readings, and its receipt would otherwise bill thinking time as away time.
 * No receipt is owed across a gap: we were not watching, so we do not know when they came back.
 */
export const GAZE_READING_GAP_RESET_MS = 3 * GAZE_SAMPLE_MS;

/**
 * Hard ceiling on gaze_away emissions per session. A stuck detector, a camera at a pathological
 * angle, or a candidate whose physiology sits outside the estimator's assumptions must not flood
 * a report with 400 flags — ten episodes says everything a reviewer could need, and past it the
 * machine keeps running (receipts for the tenth still close) but stops emitting.
 */
export const GAZE_AWAY_EMIT_CAP = 10;

export function createGazeMachine(now = 0): GazeMachine {
  return {
    confirmed: "screen",
    pending: "screen",
    pendingSince: now,
    pendingSamples: 0,
    confirmedSince: now,
    lastReadingAt: 0,
    lastAwayDirection: null,
    awayEmits: 0,
    openEmitted: false,
  };
}

/**
 * Feed one reading in, get the next machine and any events to emit.
 *
 * `status === null` means NO READING — the candidate is not answering (thinking time is free by
 * design), there is no single face to ask about, or the detector produced nothing. It leaves the
 * machine completely untouched, so silence can never accumulate into "this candidate looked away".
 */
export function observeGaze(
  machine: GazeMachine,
  status: GazeStatus | null,
  now: number,
): { machine: GazeMachine; emits: GazeEmit[] } {
  if (status === null) return { machine, emits: [] };

  const group: "screen" | "away" = status === "screen" ? "screen" : "away";
  const blindGap = machine.lastReadingAt > 0 && now - machine.lastReadingAt > GAZE_READING_GAP_RESET_MS;

  // An away episode still open across a blind gap closes SILENTLY: the answering window ended (or
  // the detector went dark) mid-episode, we did not see them come back, and a receipt would claim
  // a duration we never measured — most of it thinking time we promised was free.
  const base: GazeMachine =
    blindGap && machine.confirmed === "away"
      ? { ...machine, confirmed: "screen", confirmedSince: now, openEmitted: false }
      : machine;

  const next: GazeMachine =
    group === base.pending && !blindGap
      ? {
          ...base,
          pendingSamples: base.pendingSamples + 1,
          lastReadingAt: now,
          lastAwayDirection: group === "away" ? (status as GazeDirection) : base.lastAwayDirection,
        }
      : {
          ...base,
          pending: group,
          pendingSince: now,
          pendingSamples: 1,
          lastReadingAt: now,
          lastAwayDirection: group === "away" ? (status as GazeDirection) : base.lastAwayDirection,
        };

  if (
    next.pending === next.confirmed ||
    now - next.pendingSince < GAZE_CONFIRM_MS[next.pending] ||
    next.pendingSamples < GAZE_CONFIRM_SAMPLES[next.pending]
  ) {
    return { machine: next, emits: [] };
  }

  if (next.pending === "away") {
    const mayEmit = next.awayEmits < GAZE_AWAY_EMIT_CAP;
    const emits: GazeEmit[] = mayEmit
      ? [{ kind: "gaze_away", detail: next.lastAwayDirection ?? "left" }]
      : [];
    return {
      machine: {
        ...next,
        confirmed: "away",
        confirmedSince: now,
        awayEmits: next.awayEmits + (mayEmit ? 1 : 0),
        openEmitted: mayEmit,
      },
      emits,
    };
  }

  // away → screen: the receipt carries how long the episode we REPORTED lasted. An episode the
  // cap swallowed gets no receipt either — a gaze_return without its gaze_away would inflate the
  // report's total seconds relative to its episode count.
  const emits: GazeEmit[] = next.openEmitted
    ? [{ kind: "gaze_return", detail: formatGazeReturn(now - next.confirmedSince) }]
    : [];
  return { machine: { ...next, confirmed: "screen", confirmedSince: now, openEmitted: false }, emits };
}
